'use client';

/**
 * Toast Component
 *
 * A customizable toast notification component with variant-based styling.
 * Supports success, error, warning, info, and location variants with appropriate colors and icons.
 */
import { type VariantProps, cva } from 'class-variance-authority';
import { AlertTriangle, CheckCircle, Info, MapPin, X, XCircle } from 'lucide-react';
import React, { useEffect } from 'react';

import { cn } from '../../../lib/utils';

const toastVariants = cva(
  'group pointer-events-auto relative flex w-full items-center justify-between space-x-2 overflow-hidden rounded-lg border p-4 shadow-lg transition-all',
  {
    variants: {
      variant: {
        success: 'bg-success-0 border-success-400 text-success-600',
        error: 'bg-red-50 border-red-200 text-red-900',
        warning: 'bg-orange-50 border-orange-200 text-orange-900',
        info: 'bg-blue-50 border-blue-200 text-blue-900',
        location: 'bg-accent-purple-0 border-accent-purple-200 text-gray-900'
      }
    },
    defaultVariants: {
      variant: 'info'
    }
  }
);

const iconVariants = cva('w-5 h-5 shrink-0', {
  variants: {
    variant: {
      success: 'text-success-400',
      error: 'text-red-600',
      warning: 'text-orange-600',
      info: 'text-blue-600',
      location: 'text-blue-500'
    }
  },
  defaultVariants: {
    variant: 'info'
  }
});

const iconBackgroundVariants = cva('rounded-xl p-2 shrink-0', {
  variants: {
    variant: {
      success: 'bg-green-100',
      error: 'bg-red-100',
      warning: 'bg-orange-100',
      info: 'bg-blue-100',
      location: 'bg-accent-purple-200'
    }
  },
  defaultVariants: {
    variant: 'info'
  }
});

export interface ToastProps extends VariantProps<typeof toastVariants> {
  /**
   * Toast ID for management
   */
  id?: string;
  /**
   * Main title text
   */
  title?: string;
  /**
   * Description text
   */
  description?: string;
  /**
   * Custom icon to override default variant icon
   */
  icon?: React.ReactNode;
  /**
   * Whether to show close button
   */
  closable?: boolean;
  /**
   * Auto-dismiss duration in milliseconds (0 = no auto-dismiss)
   */
  duration?: number;
  /**
   * Callback when toast is dismissed
   */
  onDismiss?: () => void;
  /**
   * Additional CSS class
   */
  className?: string;
}

const getVariantIcon = (variant: ToastProps['variant']) => {
  switch (variant) {
    case 'success':
      return <CheckCircle className={iconVariants({ variant })} />;
    case 'error':
      return <XCircle className={iconVariants({ variant })} />;
    case 'warning':
      return <AlertTriangle className={iconVariants({ variant })} />;
    case 'location':
      return <MapPin className={iconVariants({ variant })} />;
    case 'info':
    default:
      return <Info className={iconVariants({ variant })} />;
  }
};

export function Toast({
  title,
  description,
  variant = 'info',
  icon,
  closable = true,
  duration = 5000,
  onDismiss,
  className
}: ToastProps) {
  // Auto-dismiss timer - must be called before any early returns
  useEffect(() => {
    if (duration > 0 && onDismiss) {
      const timer = setTimeout(() => {
        onDismiss();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [duration, onDismiss]);

  // Ensure we have valid props to prevent React errors
  if (!title && !description) {
    console.warn('Toast: Either title or description should be provided');
    return null;
  }

  return (
    <div className={cn(toastVariants({ variant }), className)}>
      <div className="flex items-start gap-3 flex-1">
        {/* Icon */}
        <div className={iconBackgroundVariants({ variant })}>{icon || getVariantIcon(variant)}</div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {title && <h4 className="text-sm text-neutral-800 font-semibold mb-1">{title}</h4>}
          {description && <p className="text-sm text-neutral-600 leading-relaxed">{description}</p>}
        </div>

        {/* Close button */}
        {closable && onDismiss && (
          <button
            onClick={onDismiss}
            className="flex-shrink-0 p-1 ml-2 cursor-pointer"
            aria-label="Close notification"
            type="button"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}
